import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Activity, ArrowRight, Droplet } from "lucide-react";
import useSpeechSynthesis from "@/hooks/useSpeechSynthesis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BloodCollection() {
  const [queue, setQueue] = useState(null);
  const [waitingTokens, setWaitingTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { speakWithGenderVoices } = useSpeechSynthesis();

  const fetchQueue = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/departments/blood_collection/queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQueue(response.data);
      
      // Get tokens that completed registration (ready for blood collection)
      const tokensResponse = await axios.get(`${API}/tokens?status=registered`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWaitingTokens(tokensResponse.data.sort((a, b) => parseInt(a.token_number) - parseInt(b.token_number)));
    } catch (error) {
      console.error("Failed to fetch queue", error);
    }
  };

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleCallNext = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/departments/call-next`,
        { department: "blood_collection" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const tokenNumber = response.data.token_number;
        toast.success(`Token ${tokenNumber} called!`, {
          description: "Please proceed to Blood Collection",
        });
        
        // Text-to-speech announcement in English and Telugu
        const message = `Token ${tokenNumber}, please proceed to Blood Collection Department`;
        speakWithGenderVoices(message);
        
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi6Dzvjaj0APHGy98+CbTRAMUqvr8q5jHgU7n+n0xnksBSmAx/LZkEAKE1m48N2kUxQLSKXl8r5xIQYuf9T52ZBBChZmvvTinlEQC1Kp5O+wZB4EO5rj88h1KwUqfsXu1I4+CRJatPPepVYUC0me4fC+ciEGL4DN99mPQQoVZ77z4Z9RDwtRp+Lur2IdBTug6fTGeCsFKX/H8tiNQAoVYr/04p5SEAtPpe/us2YDBY6a4fLC');
        audio.play().catch(() => {});
        fetchQueue();
      } else {
        toast.info("No tokens in queue");
      }
    } catch (error) {
      toast.error("Failed to call next token");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAndCallNext = async () => {
    if (!queue?.current_token) {
      // No current token, just call next
      await handleCallNext();
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      // Blood collection complete -> blood_done (ready for X-Ray)
      await axios.put(
        `${API}/tokens/${queue.current_token}/status`,
        { status: "blood_done" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Token ${queue.current_token} completed!`);
      
      // Wait a moment then call next
      setTimeout(async () => {
        await handleCallNext();
      }, 500);
    } catch (error) {
      toast.error("Failed to complete collection");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="blood-collection-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-red-600 flex items-center justify-center">
                <Droplet className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900">Blood Collection</h1>
                <p className="text-red-600 font-semibold mt-1">Strict Sequential Order</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => window.open('/display', '_blank')}
              data-testid="open-display-button"
              className="h-10"
            >
              <Activity className="mr-2 h-4 w-4" />
              Open Display
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button" className="h-10">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="lg:col-span-2 border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle>Current Token</CardTitle>
              <CardDescription>Patient at blood collection counter</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {queue?.current_token ? (
                <div className="space-y-4">
                  <div className="text-center py-8 bg-red-50 rounded-lg border-2 border-red-300" data-testid="current-token-display">
                    <div className="font-mono text-7xl font-black text-red-900 tracking-widest">
                      {queue.current_token}
                    </div>
                  </div>
                  <Button
                    onClick={handleCompleteAndCallNext}
                    data-testid="complete-and-next-button"
                    className="w-full h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700"
                  >
                    Complete Collection & Call Next
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-12 text-slate-400">
                    <p className="text-lg">No patient at counter</p>
                    <p className="text-sm mt-2">Click below to call the first patient</p>
                  </div>
                  <Button
                    onClick={handleCallNext}
                    disabled={loading || waitingTokens.length === 0}
                    data-testid="call-next-button"
                    className="w-full h-14 text-lg font-semibold bg-red-600 hover:bg-red-700"
                  >
                    Call Next Patient
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Waiting Queue (Sequential)</CardTitle>
              <CardDescription>{waitingTokens.length} patients in queue - ordered by token number</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="waiting-tokens-list">
                {waitingTokens.slice(0, 12).map((token, index) => (
                  <div
                    key={token.token_number}
                    className={`p-4 rounded-lg border text-center hover-lift ${
                      index === 0 ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-400' : 'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`waiting-token-${token.token_number}`}
                  >
                    <div className="font-mono text-xl font-bold text-slate-900">{token.token_number}</div>
                    <Badge
                      className={`mt-2 ${token.gender === "Male" ? "bg-indigo-100 text-indigo-800" : "bg-rose-100 text-rose-800"}`}
                    >
                      {token.gender}
                    </Badge>
                    {index === 0 && (
                      <div className="mt-2 text-xs font-semibold text-amber-700">NEXT</div>
                    )}
                  </div>
                ))}
              </div>
              {waitingTokens.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <p>No patients waiting</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}